import React, { FC } from 'react'
import { Skeleton } from 'antd'
import { getAbbreviatedNumberString } from 'components/Income/Income'
import { useCurrency } from 'contexts'

const hostsIconUrl = require('../../images/str-hosts-icon.svg')
const listingsIconUrl = require('../../images/str-listings-icon.svg')

interface OverviewBarSection {
  hostsCount?: boolean
  listingsCount?: boolean
  documentedRevenue?: boolean
  reportedRevenue?: boolean
  taxGap?: boolean
}

export interface OverviewBarConfig {
  hosts: OverviewBarSection
  listings: OverviewBarSection
}

interface STROverviewBarProps {
  isLoading: boolean
  hostsCount: number
  listingsCount: number
  documentedRevenue: number
  reportedRevenue: number
  taxGap: number
  overviewBarConfig?: OverviewBarConfig
  revenueEnabled?: boolean
  revenueRange?: { low: number; high: number }
}

const barStyle: React.CSSProperties = {
  display: 'flex',
  background: '#1C2842',
  borderRadius: '6px',
  overflow: 'hidden',
}

const sectionStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '32px',
  padding: '20px 28px',
}

const iconBoxStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '72px',
  gap: '6px',
}

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: "'Dosis', sans-serif",
  fontSize: '22px',
  fontWeight: 500,
  color: '#fff',
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
}

const hostLabelStyle: React.CSSProperties = {
  fontFamily: "'Source Sans Pro', sans-serif",
  fontSize: '14px',
  color: '#c0c5d0',
  fontWeight: 600,
  letterSpacing: '0.2px',
  whiteSpace: 'nowrap',
}

const listingLabelStyle: React.CSSProperties = {
  ...hostLabelStyle,
  color: '#7f8897',
  fontWeight: 400,
}

const valueStyle: React.CSSProperties = {
  fontFamily: "'Dosis', sans-serif",
  fontSize: '36px',
  fontWeight: 300,
  color: '#f4f4f6',
  letterSpacing: '0.4375px',
  whiteSpace: 'nowrap',
}

const taxGapValueStyle: React.CSSProperties = {
  ...valueStyle,
  fontWeight: 600,
}

const dividerStyle: React.CSSProperties = {
  width: '1px',
  background: '#fff',
  margin: '19px 0',
}

const StatItem: FC<{ label: string; value: string; isTaxGap?: boolean; variant?: 'host' | 'listing' }> = ({
  label,
  value,
  isTaxGap,
  variant = 'host',
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
    <span style={variant === 'host' ? hostLabelStyle : listingLabelStyle}>{label}</span>
    <span style={isTaxGap ? taxGapValueStyle : valueStyle}>{value}</span>
  </div>
)

const STROverviewBar: FC<STROverviewBarProps> = ({
  isLoading,
  hostsCount,
  listingsCount,
  documentedRevenue,
  reportedRevenue,
  taxGap,
  overviewBarConfig,
  revenueEnabled,
  revenueRange,
}) => {
  const { currentCurrency } = useCurrency()
  const symbol = currentCurrency === 'USD' ? '$' : currentCurrency

  if (isLoading) {
    return (
      <div style={{ background: '#1C2842', borderRadius: '6px', padding: '20px 28px' }}>
        <Skeleton active paragraph={{ rows: 1 }} />
      </div>
    )
  }

  const fmtMoney = (val: number) => `${symbol}${getAbbreviatedNumberString(val)}`

  const h = overviewBarConfig?.hosts
  const l = overviewBarConfig?.listings

  return (
    <div style={barStyle}>
      {/* HOSTS section */}
      <div style={sectionStyle}>
        <div style={iconBoxStyle}>
          <img src={hostsIconUrl} alt='Hosts' style={{ width: '28px', height: '24px' }} />
          <span style={sectionTitleStyle}>Hosts</span>
        </div>
        {(h?.hostsCount !== false) && <StatItem label='Number of hosts' value={hostsCount.toLocaleString()} variant='host' />}
        {revenueEnabled
          ? <StatItem label='Estimated Revenue' value={revenueRange ? `${fmtMoney(revenueRange.low)} – ${fmtMoney(revenueRange.high)}` : 'N/A'} variant='host' />
          : (h?.documentedRevenue !== false) && <StatItem label='Documented Revenue' value={fmtMoney(documentedRevenue)} variant='host' />
        }
        {!revenueEnabled && (h?.reportedRevenue !== false) && <StatItem label='Reported Revenue' value={fmtMoney(reportedRevenue)} variant='host' />}
        {!revenueEnabled && (h?.taxGap !== false) && <StatItem label='Tax gap' value={fmtMoney(taxGap)} isTaxGap variant='host' />}
      </div>

      <div style={dividerStyle} />

      {/* LISTINGS section */}
      <div style={sectionStyle}>
        <div style={iconBoxStyle}>
          <img src={listingsIconUrl} alt='Listings' style={{ width: '32px', height: '32px' }} />
          <span style={sectionTitleStyle}>Listings</span>
        </div>
        {(l?.listingsCount !== false) && <StatItem label='Number of listings' value={listingsCount.toLocaleString()} variant='listing' />}
        {!revenueEnabled && (l?.documentedRevenue !== false) && <StatItem label='Documented Revenue' value={fmtMoney(documentedRevenue)} variant='listing' />}
        {!revenueEnabled && (l?.reportedRevenue !== false) && <StatItem label='Reported Revenue' value={fmtMoney(reportedRevenue)} variant='listing' />}
        {!revenueEnabled && (l?.taxGap !== false) && <StatItem label='Tax gap' value={fmtMoney(taxGap)} isTaxGap variant='listing' />}
      </div>
    </div>
  )
}

export default STROverviewBar
